import { useEffect, useState } from "react";
import { Download, Check } from "lucide-react";
import { cn } from "@/lib/utils";

type PromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

let deferred: PromptEvent | null = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as PromptEvent;
  });
}

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true);

export const InstallAppButton = ({ className, label = "Download the App" }: { className?: string; label?: string }) => {
  const [installed, setInstalled] = useState(isStandalone());
  const [help, setHelp] = useState<string | null>(null);

  useEffect(() => {
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  const onClick = async () => {
    if (deferred) {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      deferred = null;
      return;
    }
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setHelp(
      ios
        ? "On iPhone: tap the Share button in Safari, then “Add to Home Screen.”"
        : "Open your browser menu (⋮) and choose “Install app” or “Add to Home screen.”",
    );
    if (window.location.pathname !== "/app") window.location.href = "/app?install=1";
  };

  if (installed)
    return (
      <span className={cn("inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold", className)}>
        <Check className="h-4 w-4" /> App installed
      </span>
    );

  return (
    <div className={className}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-4 shadow-cta hover:brightness-110 transition-all"
      >
        <Download className="h-4 w-4" /> {label}
      </button>
      {help && <p className="text-silver text-sm mt-3 max-w-sm" role="status">{help}</p>}
    </div>
  );
};
